import { useQuery } from "@tanstack/react-query";

import { planScreensApi, planScreensQueryKeys } from "../api/planScreens";
import type { OperatingModule } from "../api/operatingMode";
import { pathAllowed, screenAllowed } from "./screenAccess";

const STALE_MS = 5 * 60_000;

export interface PlanScreens {
  /**
   * Đã có câu trả lời — của máy chủ, hoặc là "không hỏi được". Trước đó đừng vẽ thứ gì phụ thuộc
   * vào gói: hiện một mục rồi rút lại trông như lỗi, và vẽ một trang rồi mới chặn là bắn đi những
   * request sẽ nhận 402.
   */
  settled: boolean;
  /** Chặn theo gói đã bật cho phòng khám này chưa. */
  enforced: boolean;
  /** Đường dẫn này có trong gói không. */
  allowsPath: (pathname: string) => boolean;
  /**
   * Màn hình mang mã này có trong gói không. CHỈ cho màn hình không có đường dẫn riêng (một tab
   * bên trong màn hình khác, ví dụ `patient-loyalty`); có đường dẫn thì hỏi {@link allowsPath} —
   * cùng câu hỏi mà menu và bộ chặn đường dẫn đang hỏi, và không cần biết mã.
   */
  allowsScreen: (code: string) => boolean;
  /** Gói có mở vùng API này không. */
  zoneOpen: (module: OperatingModule) => boolean;
}

/**
 * Gói của phòng khám cho phép những gì (ADR-0053). Một query, dùng chung cho menu, bộ chặn đường
 * dẫn và các trang có bản rút gọn.
 *
 * <p><b>Hỏng thì mở, không đóng.</b> Không tải được câu trả lời thì coi như mọi thứ được phép: đây
 * là lớp trình bày, endpoint phía sau vẫn tự chặn. Đóng khi hỏng là biến một lần chập chờn mạng
 * thành một phòng khám mất cả menu.
 */
export function usePlanScreens(): PlanScreens {
  const q = useQuery({
    queryKey: planScreensQueryKeys.mine(),
    queryFn: () => planScreensApi.mine(),
    staleTime: STALE_MS,
  });
  const view = q.data;
  return {
    settled: q.isSuccess || q.isError,
    enforced: view?.enforced ?? false,
    allowsPath: (pathname) => (view ? pathAllowed(view.screens, pathname) : true),
    allowsScreen: (code) => (view ? screenAllowed(view.screens, code) : true),
    zoneOpen: (module) => (view ? view.zones.includes(module) : true),
  };
}
